import { useState } from 'react';
import { Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import type { TaskCardType, UserType } from '@econtractor/types';
import * as apiTask from 'api/apiTask';
import { useToast } from 'context/ToastContext';
import { UserSelected } from 'modules/user';
import spinnerTitle from 'components/others/spinnerTitle';
import {
    buildUsersHistoryComment,
    type TaskUserRole,
} from './helpers/taskUsersHelpers';

interface TaskUserRoleEditFormProps {
    task: TaskCardType;
    role: TaskUserRole;
    onCancel: () => void;
    onSaved: (task: TaskCardType) => void;
}

const TaskUserRoleEditForm = ({ task, role, onCancel, onSaved }: TaskUserRoleEditFormProps) => {
    const { t } = useTranslation(['Tasks', 'common']);
    const { showToast } = useToast();
    const [user, setUser] = useState<UserType | null>(null);
    const [saving, setSaving] = useState(false);

    const roleLabel = role === 'executor' ? t('labelExecutor') : t('labelController');
    const currentId =
        role === 'executor' ? Number(task.taskExecutor || 0) : Number(task.taskController || 0);
    const canSave = Boolean(user?.ID) && Number(user?.ID) !== currentId;

    const handleSave = async () => {
        if (!user?.ID) {
            showToast(
                role === 'executor' ? t('errEmptyExecutor') : t('errEmptyController'),
                'danger',
            );
            return;
        }

        setSaving(true);
        const fromUser = role === 'executor' ? task.executorUser : task.controllerUser;
        const historyComment = buildUsersHistoryComment(
            roleLabel,
            String(fromUser?.userName || ''),
            String(user.userName || ''),
        );

        const result = await apiTask.updateTaskUsers(task.ID, {
            executorId: role === 'executor' ? Number(user.ID) : Number(task.taskExecutor || 0),
            controllerId:
                role === 'controller' ? Number(user.ID) : Number(task.taskController || 0),
            historyComment,
        });
        setSaving(false);

        if (!result?.stat || !result.task) {
            if (result?.errCode === 'EMPTY_EXECUTOR') {
                showToast(t('errEmptyExecutor'), 'danger');
                return;
            }
            if (result?.errCode === 'EMPTY_CONTROLLER') {
                showToast(t('errEmptyController'), 'danger');
                return;
            }
            if (result?.errCode === 'EXECUTOR_TIME_BUSY') {
                showToast(t('errExecutorTimeBusy'), 'danger');
                return;
            }
            showToast(t('common:messError'), 'danger');
            return;
        }

        showToast(t('messUsersUpdated'), 'success');
        onSaved(result.task);
    };

    return (
        <div className="mt-2">
            <UserSelected
                title={roleLabel}
                value={user}
                onChange={setUser}
                disabled={saving}
                maxHeight={200}
                className="mb-2"
                smallMode
                autoFocus
            />

            <div className="text-end">
                <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    className="mb-1 min70px"
                    style={{ height: 24 }}
                    disabled={saving}
                    onClick={onCancel}
                >
                    {t('common:buttons.cancel')}
                </Button>
                <Button
                    type="button"
                    variant="success"
                    size="sm"
                    className="mb-1 ms-2 min70px"
                    style={{ height: 24 }}
                    disabled={saving || !canSave}
                    onClick={() => void handleSave()}
                >
                    {spinnerTitle(t('common:buttons.change'), saving, t('common:buttons.saving'))}
                </Button>
            </div>
        </div>
    );
};

export default TaskUserRoleEditForm;
